/**
 * Sanitizes admin-authored rich HTML (e.g. exam instructions) while keeping
 * its inline `style` attributes and `<style>` blocks, which Angular's default
 * [innerHTML] sanitizer strips. Anything executable (scripts, frames, event
 * handlers, javascript: URLs, CSS expressions) is removed.
 */

const BLOCKED_TAGS = new Set([
  'script', 'iframe', 'frame', 'frameset', 'object', 'embed', 'applet',
  'link', 'meta', 'base', 'form', 'input', 'button', 'textarea', 'select',
  'option', 'svg', 'math', 'template', 'noscript',
]);

const URL_ATTRS = new Set(['href', 'src', 'xlink:href', 'action', 'formaction', 'background', 'poster']);
const SAFE_URL = /^(?:(?:https?|mailto|tel):|data:image\/(?:png|jpe?g|gif|webp|svg\+xml|bmp);|[^a-z]|[a-z0-9+.-]+(?:[^a-z0-9+.:-]|$))/i;
const UNSAFE_CSS = /expression\s*\(|javascript\s*:|vbscript\s*:|behavior\s*:|-moz-binding|@import/gi;

export function sanitizeRichHtml(html: string | null | undefined): string {
  if (!html?.trim()) {
    return '';
  }

  const doc = new DOMParser().parseFromString(`<body>${html}</body>`, 'text/html');
  const root = doc.body;

  root.querySelectorAll('*').forEach((element) => {
    const tag = element.tagName.toLowerCase();

    if (BLOCKED_TAGS.has(tag)) {
      element.remove();
      return;
    }

    if (tag === 'style') {
      element.textContent = (element.textContent ?? '').replace(UNSAFE_CSS, '');
      return;
    }

    Array.from(element.attributes).forEach((attr) => {
      const name = attr.name.toLowerCase();
      const value = attr.value.trim();

      if (name.startsWith('on') || name === 'srcdoc') {
        element.removeAttribute(attr.name);
      } else if (URL_ATTRS.has(name) && !SAFE_URL.test(value.replace(/[\s\u0000-\u001f]/g, ''))) {
        element.removeAttribute(attr.name);
      } else if (name === 'style') {
        element.setAttribute('style', value.replace(UNSAFE_CSS, ''));
      }
    });

    if (tag === 'a' && element.getAttribute('target') === '_blank') {
      element.setAttribute('rel', 'noopener noreferrer');
    }
  });

  return root.innerHTML;
}
